// Hook for auto-hiding dropdown when mouse leaves the component area
export const DropdownAutoHide = {
  mounted() {
    this.dropdownId = this.el.dataset.dropdownId
    this.dropdown = document.getElementById(this.dropdownId)
    this.hideTimeout = null
    this.hideDelay = 300 // milliseconds delay before hiding

    // Bind event handlers
    this.handleMouseEnter = this.handleMouseEnter.bind(this)
    this.handleMouseLeave = this.handleMouseLeave.bind(this)
    this.setupListeners = this.setupListeners.bind(this)

    this.setupListeners()

    // Watch for dropdown visibility changes
    if (this.dropdown) {
      this.observer = new MutationObserver(() => {
        // Re-setup listeners when dropdown visibility changes
        this.setupListeners()
      })

      this.observer.observe(this.dropdown, {
        attributes: true,
        attributeFilter: ["style", "class"],
      })
    }
  },

  updated() {
    // When LiveView updates the DOM (e.g., tab change), ensure dropdown is properly hidden
    // The dropdown might appear due to LiveView restoring previous state
    this.dropdown = document.getElementById(this.dropdownId)

    // Clear any pending timeouts from before the update
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout)
      this.hideTimeout = null
    }

    // Force hide the dropdown on updates to prevent it from showing unexpectedly
    if (this.dropdown) {
      const computedStyle = window.getComputedStyle(this.dropdown)
      if (computedStyle.display !== "none") {
        // Dropdown is visible after update, but it shouldn't be
        // This happens when switching tabs
        this.dropdown.style.display = "none"
        if (!this.dropdown.classList.contains("hidden")) {
          this.dropdown.classList.add("hidden")
        }
      }
    }

    this.setupListeners()
  },

  setupListeners() {
    // Add event listeners to both the trigger element and dropdown
    this.el.addEventListener("mouseenter", this.handleMouseEnter)
    this.el.addEventListener("mouseleave", this.handleMouseLeave)

    if (this.dropdown) {
      this.dropdown.addEventListener("mouseenter", this.handleMouseEnter)
      this.dropdown.addEventListener("mouseleave", this.handleMouseLeave)
    }
  },

  handleMouseEnter() {
    // Cancel any pending hide operation
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout)
      this.hideTimeout = null
    }
  },

  handleMouseLeave(event) {
    // Check if we're moving to the other element (trigger -> dropdown or dropdown -> trigger)
    const relatedTarget = event.relatedTarget

    // If moving to dropdown from trigger or vice versa, don't hide
    if (relatedTarget) {
      if (
        this.dropdown &&
        (this.dropdown.contains(relatedTarget) ||
          this.el.contains(relatedTarget))
      ) {
        return
      }
    }

    // Schedule hiding the dropdown after a delay
    this.hideTimeout = setTimeout(() => {
      // Check if dropdown is actually visible
      // Phoenix JS.toggle sets display: block when showing
      const computedStyle = this.dropdown
        ? window.getComputedStyle(this.dropdown)
        : null
      const isVisible =
        this.dropdown && computedStyle && computedStyle.display !== "none"

      if (isVisible) {
        // Hide the dropdown using both display and class
        // This ensures it works with Phoenix LiveView's JS.toggle
        this.dropdown.style.display = "none"

        // Also add hidden class for consistency
        if (!this.dropdown.classList.contains("hidden")) {
          this.dropdown.classList.add("hidden")
        }
      }
    }, this.hideDelay)
  },

  destroyed() {
    // Clean up event listeners
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout)
    }

    if (this.observer) {
      this.observer.disconnect()
    }

    this.el.removeEventListener("mouseenter", this.handleMouseEnter)
    this.el.removeEventListener("mouseleave", this.handleMouseLeave)

    if (this.dropdown) {
      this.dropdown.removeEventListener("mouseenter", this.handleMouseEnter)
      this.dropdown.removeEventListener("mouseleave", this.handleMouseLeave)
    }
  },
}

export default DropdownAutoHide
